'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { KeyboardEvent, MouseEvent, ReactNode, TouchEvent } from 'react';
import React, { useRef, useState } from 'react';

const DRAG_OFFSET = 250;

function getClientX(event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) {
  return 'touches' in event ? (event.touches[0]?.clientX ?? 0) : event.clientX;
}

export function Slideshow({ children }: { children: ReactNode[] }): React.JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartX = useRef(0);
  const totalSlides = children.length;

  const handlePrev = () => {
    setCurrentIndex(index => Math.max(index - 1, 0));
  };

  const handleNext = () => {
    setCurrentIndex(index => Math.min(index + 1, totalSlides - 1));
  };

  const handleDragStart = (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    dragStartX.current = getClientX(event);
    setIsDragging(true);
  };

  const handleDragMove = (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    setDragOffset(getClientX(event) - dragStartX.current);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    if (dragOffset > DRAG_OFFSET) handlePrev();
    if (dragOffset < -DRAG_OFFSET) handleNext();

    setIsDragging(false);
    setDragOffset(0);
    dragStartX.current = 0;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      handlePrev();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      handleNext();
    }
  };

  return (
    <div>
      <div className="relative">
        <button
          aria-label="Previous slide"
          className={`absolute top-[40%] z-10 -left-2 hidden size-9 rounded-full bg-card text-secondary-foreground shadow-[0_4px_8px_0_rgba(100,102,106,.1),0_1px_4px_0_rgba(100,102,106,.36)] transition-opacity duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:block md:-left-6 ${currentIndex > 0 ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          disabled={currentIndex === 0}
          onClick={handlePrev}
          type="button"
        >
          <ChevronLeft className="m-auto" />
        </button>
        <div
          className="relative w-full overflow-hidden select-none"
          onMouseDown={handleDragStart}
          onMouseLeave={handleDragEnd}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onTouchEnd={handleDragEnd}
          onTouchMove={handleDragMove}
          onTouchStart={handleDragStart}
        >
          <div
            className={`flex items-stretch ${isDragging ? 'transition-none' : 'transition-[transform] duration-500 ease-in-out'}`}
            data-testid="slideshow-track"
            onKeyDown={handleKeyDown}
            style={{
              transform: `translateX(calc(-${(currentIndex * 100) / totalSlides}% + ${dragOffset}px))`,
              width: `${totalSlides * 100}%`,
            }}
            tabIndex={0}
          >
            {children.map((child, index) => (
              <div className="w-full pr-4" key={index}>
                {child}
              </div>
            ))}
          </div>
        </div>
        <button
          aria-label="Next slide"
          className={`absolute top-[40%] right-0 z-10 hidden size-9 rounded-full bg-card text-secondary-foreground shadow-[0_4px_8px_0_rgba(100,102,106,.1),0_1px_4px_0_rgba(100,102,106,.36)] transition-opacity duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:block ${currentIndex < totalSlides - 1 ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          disabled={currentIndex >= totalSlides - 1}
          onClick={handleNext}
          type="button"
        >
          <ChevronRight className="m-auto" />
        </button>
      </div>
      {totalSlides > 1 && (
        <div className="mt-7 flex justify-center">
          {Array.from({ length: totalSlides }, (_, index) => (
            <button
              aria-label={`Go to slide ${index + 1}`}
              className="size-12 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              key={index}
              onClick={() => setCurrentIndex(index)}
              type="button"
            >
              {/* Legacy: 10px dot sits at the top-left origin of the 48px box (no centering). */}
              <span
                className={`block size-2.5 rounded-full transition-colors ${index === currentIndex ? 'bg-primary' : 'border border-[var(--content-neutral-dark)]'}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
